const MAX_IMAGE_WIDTH = 1920
const MAX_IMAGE_HEIGHT = 1920
const JPEG_QUALITY = 0.85
const MAX_PHOTOS_PER_BATCH = 9
const MAX_IMAGE_FILE_SIZE = 20 * 1024 * 1024 // 单张图片最大 20MB（压缩前）
const MAX_VIDEO_FILE_SIZE = 50 * 1024 * 1024 // 单个视频最大 50MB
const MAX_VIDEO_COUNT = 9 // 每次最多上传 9 个视频
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg']
const ALLOWED_VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/x-m4v']

export interface CompressResult {
  dataUrl: string
  blob: Blob
  originalSize: number
  compressedSize: number
  width: number
  height: number
}

export function validateImageFile(file: File): string | null {
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    return '仅支持 JPG、PNG、WebP 格式的图片'
  }
  return null
}

export function validateVideoFile(file: File): string | null {
  if (!ALLOWED_VIDEO_TYPES.includes(file.type)) {
    return '仅支持 MP4、MOV 格式的视频'
  }
  return null
}

export function validatePhotoCount(currentCount: number, addCount: number): string | null {
  if (currentCount + addCount > MAX_PHOTOS_PER_BATCH) {
    return `每批次最多上传 ${MAX_PHOTOS_PER_BATCH} 张照片`
  }
  return null
}

export function validateImageSize(file: File): string | null {
  if (file.size > MAX_IMAGE_FILE_SIZE) {
    return `图片大小不能超过 ${formatFileSize(MAX_IMAGE_FILE_SIZE)}，当前文件 ${formatFileSize(file.size)}`
  }
  return null
}

export function validateVideoSize(file: File): string | null {
  if (file.size > MAX_VIDEO_FILE_SIZE) {
    return `视频大小不能超过 ${formatFileSize(MAX_VIDEO_FILE_SIZE)}，当前文件 ${formatFileSize(file.size)}`
  }
  return null
}

export function validateVideoCount(currentCount: number, addCount: number): string | null {
  if (currentCount + addCount > MAX_VIDEO_COUNT) {
    return `每次只能上传 ${MAX_VIDEO_COUNT} 个视频`
  }
  return null
}

export { MAX_IMAGE_FILE_SIZE, MAX_VIDEO_FILE_SIZE, MAX_VIDEO_COUNT, MAX_PHOTOS_PER_BATCH }

export function compressImage(file: File): Promise<CompressResult> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = (event) => {
      const img = new Image()
      img.onload = () => {
        let { width, height } = img

        if (width > MAX_IMAGE_WIDTH || height > MAX_IMAGE_HEIGHT) {
          const scale = Math.min(MAX_IMAGE_WIDTH / width, MAX_IMAGE_HEIGHT / height)
          width = Math.round(width * scale)
          height = Math.round(height * scale)
        }

        const canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext('2d')
        if (!ctx) {
          reject(new Error('Canvas context not available'))
          return
        }
        ctx.drawImage(img, 0, 0, width, height)

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              // 释放 canvas 资源防止移动端内存耗尽
              canvas.width = 0
              canvas.height = 0
              reject(new Error('图片压缩失败'))
              return
            }
            const dataUrl = canvas.toDataURL('image/jpeg', JPEG_QUALITY)
            // 释放 canvas 资源防止移动端内存耗尽
            canvas.width = 0
            canvas.height = 0
            resolve({
              dataUrl,
              blob,
              originalSize: file.size,
              compressedSize: blob.size,
              width,
              height,
            })
          },
          'image/jpeg',
          JPEG_QUALITY,
        )
      }
      img.onerror = () => reject(new Error('图片加载失败'))
      img.src = event.target?.result as string
    }

    reader.onerror = () => reject(new Error('文件读取失败'))
    reader.readAsDataURL(file)
  })
}

export function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (event) => resolve(event.target?.result as string)
    reader.onerror = () => reject(new Error('文件读取失败'))
    reader.readAsDataURL(file)
  })
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + 'B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + 'KB'
  return (bytes / (1024 * 1024)).toFixed(1) + 'MB'
}
